import { JOBS_ENDPOINT, JOB_STATUS_COLOR_MAP, JOB_KIND_COLOR_MAP, getJobKindChipSx } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import { useTranslation } from "react-i18next";
import { useHasPermission, useHasAnyPermission, useTenantName } from "../../../../contexts/auth";
import { formatCounterValue, formatDate, formatDateTimeInline, formatQuantity } from "../../../../utils";
import { JobTask, JobUsedPart } from "../../../../types/jobs";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import AttachmentChip from "../../../../components/ui/attachmentChip/attachmentChip.component";
import { AddressDisplay } from "../../../../components/ui/addressDisplay/addressDisplay.component";
import { EquipmentConfigurationChips } from "../../../../components/ui/equipmentConfigurationChips/equipmentConfigurationChips.component";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  UserDisplay,
} from "../../../../components/ui/detailView";
import { StatusChip } from "../../../../components/ui/statusChip/statusChip.component";
import { JobStatusDurations } from "./jobStatusDurations.component";

type JobBasicInfoProps = {
  previewId?: string | number;
};

/** Issue / service chips, in the colour configured for each. */
const ColoredChips = ({ items }: { items: { label: string; color?: string | null }[] }) => {
  if (items.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
      {items.map((item, index) => (
        <Chip
          key={index}
          label={item.label}
          size="small"
          sx={
            item.color
              ? { bgcolor: item.color, color: "white", "& .MuiChip-label": { color: "white" } }
              : { bgcolor: "rgba(128, 128, 128, 0.25)", color: "text.secondary" }
          }
        />
      ))}
    </Box>
  );
};

// Canonical display order for counter kinds - Black pages, then Color pages,
// then Scanned pages - regardless of the order they come back from the API.
const COUNTER_KIND_ORDER: Record<string, number> = { BP: 0, CP: 1, SP: 2 };

type CounterTableRow = {
  id: string | number;
  kind: string;
  kind_display?: string;
  value: string;
  read_at: string;
};

// A task's counter readings as a small table - either the ones assigned to
// this specific JobTask (JobTask.counters - a task can have several, e.g. one
// per kind) or the machine's latest readings from before this job
// (JobTask.latest_counters).
const TaskCounterTable = ({ counters }: { counters?: CounterTableRow[] }) => {
  const { t } = useTranslation();

  if (!counters || counters.length === 0) {
    return null;
  }

  const sortedCounters = [...counters].sort(
    (a, b) => (COUNTER_KIND_ORDER[a.kind] ?? 99) - (COUNTER_KIND_ORDER[b.kind] ?? 99)
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Kind")}</TableCell>
                <TableCell>{t("Value")}</TableCell>
                <TableCell>{t("Read at")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCounters.map((counter) => (
                <TableRow key={counter.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell>{counter.kind_display || "—"}</TableCell>
                  <TableCell>{formatCounterValue(counter.value)}</TableCell>
                  <TableCell>{counter.read_at ? formatDateTimeInline(counter.read_at) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

// Parts used on this specific JobTask - just what was used and how much,
// nothing else (no pricing/order details; that lives on the Part/Stock pages).
const TaskUsedPartsTable = ({ usedParts }: { usedParts: JobUsedPart[] }) => {
  const { t } = useTranslation();

  if (!usedParts || usedParts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Part")}</TableCell>
                <TableCell>{t("Quantity")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usedParts.map((usedPart) => (
                <TableRow key={usedPart.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell>{usedPart.part?.name || "—"}</TableCell>
                  <TableCell>
                    {`${formatQuantity(usedPart.quantity)} ${usedPart.part?.unit ?? ""}`.trim()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export const JobBasicInfo = ({ previewId }: JobBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(JOBS_ENDPOINT);
  const [job, setJob] = useState<any | null>(null);
  const canViewJobs = useHasAnyPermission(["service.view_job", "service.view_own_job"]);
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  // Equipment with no installation address is back with us, not at the
  // client's site.
  const tenantName = useTenantName();
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchJob = async () => {
      if (entityId) {
        const response = await get(entityId);
        if (response.data) {
          setJob(response.data);
        }
      }
    };

    const handleJobUpdated = () => {
      fetchJob();
    };

    const handleCounterAdded = () => {
      fetchJob();
    };

    window.addEventListener("jobUpdated", handleJobUpdated);
    window.addEventListener("equipmentCounterAdded", handleCounterAdded);
    fetchJob();
    return () => {
      window.removeEventListener("jobUpdated", handleJobUpdated);
      window.removeEventListener("equipmentCounterAdded", handleCounterAdded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canViewJobs) {
    return null;
  }

  if (!job) return <DetailPage loading />;

  const tasks: JobTask[] = job.tasks ?? job.services ?? [];

  // A job reported through the client portal has no staff author - a portal
  // contact is not a user - so name the client's contact instead.
  const createdBy = job.reported_by_portal_contact ? (
    <UserDisplay
      size="small"
      user={{
        first_name: job.reported_by_portal_contact.full_name,
        email: job.reported_by_portal_contact.email,
      }}
      suffix={<Chip label={t("Client portal")} size="small" variant="outlined" />}
    />
  ) : (
    job.created_by && <UserDisplay size="small" user={job.created_by} />
  );

  return (
    <DetailPage>
      <DetailHeader
        icon={<BuildOutlinedIcon />}
        title={job.number || job.id}
        subtitle={job.client && (job.client.short_name || job.client.name)}
        chips={
          <>
            {job.status_display && (
              <StatusChip label={job.status_display} value={job.status} colorMap={JOB_STATUS_COLOR_MAP} />
            )}
            <Chip
              label={job.kind_display || "—"}
              color={job.kind === "C" ? (JOB_KIND_COLOR_MAP[job.kind] ?? "default") : undefined}
              // getJobKindChipSx returns a theme callback, so it can't be spread.
              sx={[{ fontWeight: 600 }, (job.kind !== "C" && getJobKindChipSx(job.kind)) || {}]}
              size="small"
            />
          </>
        }
      >
        {job.rejection_reason && (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("Rejection reason")}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {job.rejection_reason}
            </Typography>
          </Alert>
        )}
      </DetailHeader>

      <DetailGrid>
        <DetailSection title={t("Basic Information")}>
          {job.client && (
            <DetailRow label={t("Client")}>
              <EntityReferenceLink
                entityType="client"
                entityId={job.client?.id || job.client_id}
                label={job.client.short_name || job.client.name}
                canPreview={canViewClients}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          {job.planned_for && <DetailRow label={t("Planned For")} value={formatDate(job.planned_for)} />}
          {job.description && <DetailRow label={t("Description")} value={job.description} multiline />}
          {job.complaint_for_number && (
            <DetailRow label={t("Complaint For")}>
              <EntityReferenceLink
                entityType="job"
                entityId={job.complaint_for_id}
                label={job.complaint_for_number}
                canPreview={canViewJobs}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          <DetailRow label={t("Assigned To")}>
            {job.assigned_to_names && job.assigned_to_names.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {job.assigned_to_names.map((name: string, index: number) => (
                  <Chip key={index} label={name} size="small" variant="outlined" />
                ))}
              </Box>
            ) : null}
          </DetailRow>
          <DetailRow label={t("Finished At")} value={formatDateTimeInline(job.finished_at)} />
        </DetailSection>
      </DetailGrid>

      <JobStatusDurations timeline={job.status_timeline} />

      {tasks.length > 0 && (
        <>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mt: 1 }}>
            {t("Tasks")} ({tasks.length})
          </Typography>
          {tasks.map((task, index) => {
            const usedParts = (job.used_parts ?? []).filter(
              (usedPart: JobUsedPart) => String(usedPart.job_task_id) === String(task.id)
            );
            const installationAddress = task.equipment?.installation_address;
            const remarks = task.remarks?.trim();
            // Under the machine they concern - or where it would be, for a task
            // without equipment.
            const remarksRow = remarks && (
              <DetailRow label={t("Remarks")} alignTop>
                <Alert severity="warning" sx={{ width: "100%", whiteSpace: "pre-wrap", py: 0 }}>
                  {task.remarks}
                </Alert>
              </DetailRow>
            );

            return (
              <DetailSection
                key={task.id}
                title={task.equipment?.full_name || `${t("Task")} ${index + 1}`}
                // Outlined so the next technician at this machine can't miss them.
                warning={Boolean(remarks)}
                action={
                  task.finished_at && (
                    <Typography variant="caption" color="text.secondary">
                      {t("Finished At")}: {formatDateTimeInline(task.finished_at)}
                    </Typography>
                  )
                }
              >
                <DetailRow label={t("Issues")}>
                  <ColoredChips
                    items={(task.issues ?? []).map(
                      (issue: { code?: string | null; title?: string; color?: string | null }) => {
                        const title = issue.title || "—";
                        return { label: issue.code ? `${title} (${issue.code})` : title, color: issue.color };
                      }
                    )}
                  />
                </DetailRow>
                <DetailRow label={t("Services")}>
                  <ColoredChips
                    items={(task.services ?? []).map((svc: { title?: string; color?: string | null }) => ({
                      label: svc.title || "—",
                      color: svc.color,
                    }))}
                  />
                </DetailRow>
                {!task.equipment && remarksRow}
                {task.equipment && (
                  <>
                    <DetailRow label={t("Equipment")}>
                      <EntityReferenceLink
                        entityType="equipment"
                        entityId={task.equipment.id}
                        label={task.equipment.full_name || "—"}
                        canPreview={canViewEquipment}
                        stopPropagation={false}
                      />
                    </DetailRow>
                    {remarksRow}
                    <DetailRow label={t("Installation Address")} alignTop>
                      {installationAddress ? (
                        <Box sx={{ width: "100%" }}>
                          <AddressDisplay
                            address={installationAddress}
                            title={`${t("Installation Address")} & ${t("Opening Hours")}`}
                            withDetails
                            withContact
                            collapsible
                          />
                        </Box>
                      ) : (
                        tenantName && (
                          <Typography variant="subtitle1" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                            {tenantName}
                          </Typography>
                        )
                      )}
                    </DetailRow>
                    {Array.isArray(task.equipment.attribute_values) && task.equipment.attribute_values.length > 0 && (
                      <DetailRow label={t("Configuration")}>
                        <EquipmentConfigurationChips attributeValues={task.equipment.attribute_values} />
                      </DetailRow>
                    )}
                  </>
                )}
                {task.equipment && task.latest_counters && !(task.counters?.length > 0) && (
                  // The machine's latest readings until this job records its
                  // own - from then on the task's counters take their place.
                  <DetailRow label={t("Latest counter readings")} alignTop>
                    {task.latest_counters.length > 0 ? (
                      <TaskCounterTable counters={task.latest_counters} />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        {t("No counter reading yet")}
                      </Typography>
                    )}
                  </DetailRow>
                )}
                {task.counters && task.counters.length > 0 && (
                  <DetailRow label={t("Counter")} alignTop>
                    <TaskCounterTable counters={task.counters} />
                  </DetailRow>
                )}
                {usedParts.length > 0 && (
                  <DetailRow label={t("Used parts")} alignTop>
                    <TaskUsedPartsTable usedParts={usedParts} />
                  </DetailRow>
                )}
                {task.description && <DetailRow label={t("Description")} value={task.description} multiline />}
                {task.attachments && task.attachments.length > 0 && (
                  <DetailRow label={t("Attachments")}>
                    {task.attachments.map((attachment) => (
                      <AttachmentChip
                        key={attachment.id}
                        url={attachment.attachment || undefined}
                        label={
                          attachment.attachment
                            ? attachment.attachment.replace(/^.*\//, "")
                            : t("Attachment")
                        }
                      />
                    ))}
                  </DetailRow>
                )}
              </DetailSection>
            );
          })}
        </>
      )}

      {job.client?.address && (
        <AddressDisplay
          address={job.client.address}
          title={`${t("Client address")} & ${t("Opening Hours")}`}
          withDetails
          withContact
          collapsible
          defaultExpanded={false}
        />
      )}

      <DetailMetadata
        createdAt={job.created_at}
        createdBy={createdBy}
        updatedAt={job.updated_at}
        items={[
          {
            icon: <SyncAltOutlinedIcon />,
            label: t("Status Changed At"),
            value: formatDateTimeInline(job.status_changed_at),
          },
        ]}
      />
    </DetailPage>
  );
};
